import { Button, Typography } from '@breetometer/components';
import { classNameMerger } from '@breetometer/utils';
import { ApiAccessStatusIcon, InfoIcon, VisibilityIcon, WalletCardIcon } from 'BreetAssets';
import { Tooltip, TooltipContent } from 'BreetComponents';
import { type CURRENCYCODETYPE, CURRENCYCONFIG, type CURRENCYSYMBOLTYPE, type ReceiveCurrencyType } from 'BreetHelpers';
import { useCryptoSettingsApi, useUserCurrencyProps, useWalletBalance, useWithdrawRequestApi } from 'BreetHooks';
import { selectIsFiatBalanceHidden, setIsFiatBalanceHidden, useAppDispatch, useAppSelector } from 'BreetRedux';
import { useCallback, useMemo } from 'react';

import type { IWalletBalance } from './WalletBalance.helpers';
import WalletBalanceSkeleton from './WalletBalance.Skeleton';

const WalletBalance = ({ withdraw, fiat, isApiAccess }: IWalletBalance) => {
	const { withdrawFunds, withdrawFundsUsd, fiatMeta } = useWithdrawRequestApi();
	const { currentReceiveCurrency, manageUSD, isUpdating: isLoadingCryptoSettings } = useCryptoSettingsApi();

	const dispatch = useAppDispatch();

	const currencyProps = useUserCurrencyProps();

	const usdEnabled = currentReceiveCurrency === 'usd';
	const isUSD = fiat === 'USD';
	const hideAmount = useAppSelector(selectIsFiatBalanceHidden);
	const hiddenAmount = '*******';

	const { isWalletBalanceFetching, isWalletBalanceLoading, getBalance, wallets } = useWalletBalance();

	const balance = getBalance(fiat);

	const walletBalance = wallets && !hideAmount ? balance : hiddenAmount;

	const handleVisibility = useCallback(() => {
		if (withdraw) return;

		dispatch(setIsFiatBalanceHidden(!hideAmount));
	}, [dispatch, hideAmount, withdraw]);

	const uiConfigObj: Record<
		CURRENCYCODETYPE,
		{ currency: CURRENCYCODETYPE; symbol: CURRENCYSYMBOLTYPE; withdrawHandler?: () => void }
	> = {
		USD: {
			currency: CURRENCYCONFIG.USD.code,
			symbol: CURRENCYCONFIG.USD.symbol,
			withdrawHandler: withdrawFundsUsd,
		},
		NGN: {
			currency: CURRENCYCONFIG.NGN.code,
			symbol: CURRENCYCONFIG.NGN.symbol,
			withdrawHandler: withdrawFunds,
		},
		GHS: {
			currency: CURRENCYCONFIG.GHS.code,
			symbol: CURRENCYCONFIG.GHS.symbol,
			withdrawHandler: withdrawFunds,
		},
	};

	const uiConfig = uiConfigObj[fiat];

	const linkTitle = useMemo(() => {
		if (isUSD) {
			return usdEnabled ? 'Deactivate' : 'Activate';
		}
		return usdEnabled ? 'Activate' : 'Deactivate';
	}, [isUSD, usdEnabled]);

	const handleTooltipClick = useCallback(() => {
		try {
			if (!currencyProps) {
				console.warn('Currency props not found');
				return;
			}
			const targetCurrency = usdEnabled ? (currencyProps.code.toLowerCase() as ReceiveCurrencyType) : 'usd';
			manageUSD({ receiveCurrency: targetCurrency });
		} catch (error) {
			console.error('Error managing USD:', error);
		}
	}, [currencyProps, usdEnabled, manageUSD]);

	if (isWalletBalanceLoading) {
		return (
			<WalletBalanceSkeleton
				withdraw={withdraw}
				isUsd={isUSD}
			/>
		);
	}

	return (
		<div
			className={classNameMerger('card walletbalance', fiat === 'USD' && 'usd-card', fiat === 'GHS' && 'ghs-card')}
			data-testid='wallet-balance'
		>
			<div className='walletbalance_icon-section'>
				<WalletCardIcon
					className={classNameMerger('walletbalance_icon', fiat === 'NGN' && 'svg-dark svg-dark-outline svg-dark-fill')}
					variant={fiat}
				/>

				{isApiAccess && <ApiAccessStatusIcon />}
			</div>

			<div className='walletbalance_content'>
				<div className='walletbalance_content_header'>
					<Typography
						as='span'
						onClick={handleVisibility}
						className='cursor_pointer'
					>
						{uiConfig.currency.toUpperCase()} Balance
					</Typography>

					{withdraw ? (
						<Tooltip
							tipElement={
								<TooltipContent
									content={`Every settled crypto-to-${isUSD ? 'usd' : fiatMeta.currencyShort} transaction will be credited here.`}
									linkTitle={linkTitle}
									onLinkClick={handleTooltipClick}
									loading={isLoadingCryptoSettings}
								/>
							}
						>
							<InfoIcon
								variant='square'
								width={15.42}
								height={15.42}
							/>
						</Tooltip>
					) : (
						<VisibilityIcon
							show={!hideAmount}
							className='walletbalance_content_visibility cursor_pointer'
							onClick={handleVisibility}
						/>
					)}

					{isWalletBalanceFetching && <div className='button-spinner' />}
				</div>

				<div className='walletbalance_content_body'>
					<Typography as='h6'>
						<span>{hideAmount ? '' : uiConfig.symbol}</span>
						{walletBalance}
					</Typography>

					{withdraw && (
						<Button
							label='Withdraw'
							variant={isUSD || fiat === 'GHS' ? 'secondary' : 'tertiary'}
							onClick={uiConfig.withdrawHandler}
							className='child_nudge'
						/>
					)}
				</div>
			</div>
		</div>
	);
};

export default WalletBalance;
